import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createBillComCustomer,
    createPractice,
    fetchPracticeDetail,
    previewSparkAccountRequest,
    sendSparkAccountRequest,
    updatePracticeDetail,
    type CreateBillComCustomerInput,
    type PracticeDetailUpdate,
    type SparkAccountRequestOverrides
} from '../services/practiceDetail';
import {
    ACCOUNTS_PRACTICES_INFINITE_KEY,
    ACCOUNTS_PRACTICES_LIST_KEY,
    PRACTICE_DETAIL_KEY,
    PRACTICE_RISK_STATUSES_KEY,
    PRACTICE_TIMELINE_KEY,
    SPARK_ACCOUNT_REQUEST_PREVIEW_KEY
} from './queryKeys';

export function usePracticeDetail(practiceId: string | undefined) {
    const query = useQuery({
        queryKey: PRACTICE_DETAIL_KEY(practiceId ?? ''),
        queryFn: () => fetchPracticeDetail(practiceId as string),
        enabled: Boolean(practiceId),
        staleTime: 60_000
    });

    return {
        practice: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch
    };
}

export function useUpdatePracticeDetail(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (changes: PracticeDetailUpdate) => updatePracticeDetail(practiceId, changes),
        onSuccess: (updated, changes) => {
            queryClient.setQueryData(PRACTICE_DETAIL_KEY(practiceId), updated);
            // name/city changes can affect the practices list row, so it's
            // invalidated too rather than just the single-record cache -
            // both the paged and the infinite-mode list query.
            queryClient.invalidateQueries({ queryKey: ACCOUNTS_PRACTICES_LIST_KEY });
            queryClient.invalidateQueries({ queryKey: ACCOUNTS_PRACTICES_INFINITE_KEY });
            // The save just appended a practice.updated entry, so the aside's
            // activity card is stale the moment this resolves. Invalidated
            // explicitly: it shares the practices key prefix but not the list
            // key, so the line above does not reach it.
            queryClient.invalidateQueries({ queryKey: PRACTICE_TIMELINE_KEY(practiceId) });
            // The risk-statuses overlay is a separate global list (see
            // usePracticeRiskStatuses) - only worth invalidating when this
            // save actually touched the risk block, since every other Details
            // tab save (Practice/Doctor/Account) hits this same mutation too.
            if (changes.risk) {
                queryClient.invalidateQueries({ queryKey: PRACTICE_RISK_STATUSES_KEY });
            }
        }
    });
}

/**
 * Creating a Bill.com customer only ever writes to practice_monday_data
 * (billId) on the backend - it never touches the practice row's own
 * updatedAt. usePracticeDetailsForm resets its dirty edits only when
 * `${practice.id}:${practice.updatedAt}` changes, so this invalidation is
 * safe to fire even while the Details form has unrelated unsaved edits
 * pending: the refetched practice carries the same updatedAt, the form's
 * reset effect never fires, and the new billId simply flows into
 * originalValues underneath whatever the user is still editing.
 */
export function useCreateBillComCustomer(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateBillComCustomerInput) => createBillComCustomer(practiceId, input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRACTICE_DETAIL_KEY(practiceId) });
        }
    });
}

/**
 * The dryRun preview backing RequestSparkAccountModal's email preview -
 * `overrides` should already be the debounced value (the modal debounces the
 * three raw inputs itself before calling this), so every keystroke doesn't
 * fire its own request. Refetches automatically whenever `overrides` changes
 * since it's baked into the query key (see SPARK_ACCOUNT_REQUEST_PREVIEW_KEY).
 * `staleTime: 0` because a stale preview from a moment ago is actively
 * misleading here, unlike the record-detail queries above.
 */
export function useSparkAccountRequestPreview(
    practiceId: string,
    overrides: SparkAccountRequestOverrides
) {
    const normalized = {
        requestedUsername: overrides.requestedUsername ?? '',
        doctorType: overrides.doctorType ?? '',
        website: overrides.website ?? ''
    };

    return useQuery({
        queryKey: SPARK_ACCOUNT_REQUEST_PREVIEW_KEY(practiceId, normalized),
        queryFn: () => previewSparkAccountRequest(practiceId, normalized),
        staleTime: 0,
        retry: false
    });
}

/**
 * Sends the Spark account request email for real. Writes nothing to the
 * practice record - see sendSparkAccountRequest - so there's no cache to
 * invalidate on success, unlike useCreateBillComCustomer above.
 */
export function useSendSparkAccountRequest(practiceId: string) {
    return useMutation({
        mutationFn: (overrides: SparkAccountRequestOverrides) =>
            sendSparkAccountRequest(practiceId, overrides)
    });
}

export function useCreatePractice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: PracticeDetailUpdate) => createPractice(body),
        onSuccess: () => {
            // No single-record cache to seed yet - the new practice's detail
            // page fetches it fresh. Only the list needs invalidating so the
            // new row shows up without a manual refresh, in whichever mode
            // the table is currently in.
            queryClient.invalidateQueries({ queryKey: ACCOUNTS_PRACTICES_LIST_KEY });
            queryClient.invalidateQueries({ queryKey: ACCOUNTS_PRACTICES_INFINITE_KEY });
        }
    });
}
